import React, { useState, useEffect, useRef } from 'react';
import Highlighter from 'react-highlight-words';
import { Input, Button, Space, Table, Tag, Popconfirm, Tooltip, Select, Modal, Form } from 'antd';
import { DeleteOutlined, SearchOutlined, EditOutlined } from '@ant-design/icons';
import InputRules from '../../data/InputRules';
import { connect } from 'react-redux';
import { Actions } from '../../store/actions';
import BankFormStatus from '../../data/BankFormStatus';

const BankFormManagement = ({
	bankForms,
	bankUsers,
	banks,
	fetchBankForms,
	fetchBankUsers,
	fetchBanks,
	updateBankFormStatus,
	updateBankForm,
	updateBankFormAgent,
	deleteBankForm,
}) => {
	const [searchText, setSearchText] = useState('');
	const [searchedColumn, setSearchedColumn] = useState('');
	const [isEditModalOpen, setIsEditModalOpen] = useState();
	const [editBankForm, setEditBankForm] = useState();
	const editBankFormRef = useRef(null);

	useEffect(() => {
		fetchBanks();
		fetchBankForms();
		fetchBankUsers();
	}, []);

	const getColumnSearchProps = (dataIndex) => ({
		filterDropdown: function filterDropdown({ setSelectedKeys, selectedKeys, confirm, clearFilters }) {
			return (
				<div style={{ padding: 8 }}>
					<Input
						placeholder={`Search ${dataIndex}`}
						value={selectedKeys[0]}
						onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
						onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
						style={{ width: 188, marginBottom: 8, display: 'block' }}
					/>
					<Space>
						<Button
							type="primary"
							onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
							icon={<SearchOutlined />}
							size="small"
							style={{ width: 90 }}
						>
							Search
						</Button>
						<Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
							Reset
						</Button>
					</Space>
				</div>
			);
		},
		filterIcon: function filter(filtered) {
			return <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />;
		},
		onFilter: (value, record) =>
			record[dataIndex] ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()) : '',
		render: function render(text) {
			return searchedColumn === dataIndex ? (
				<Highlighter
					highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
					searchWords={[searchText]}
					autoEscape
					textToHighlight={text ? text.toString() : ''}
				/>
			) : (
				text
			);
		},
	});

	const handleSearch = (selectedKeys, confirm, dataIndex) => {
		confirm();
		setSearchText(selectedKeys[0]);
		setSearchedColumn(dataIndex);
	};

	const handleReset = (clearFilters) => {
		clearFilters();
		setSearchText('');
	};

	const onDelete = async (id) => {
		await deleteBankForm(id);
		fetchBankForms();
	};

	const updateForm = async (values) => {
		await updateBankForm(editBankForm._id, values);
		editBankFormRef.current.resetFields();
		setIsEditModalOpen(false);
		fetchBankForms();
	};

	const onFinishFailed = (errorInfo) => {
		message.error('Something went wrong!');
	};

	const columns = [
		{
			title: 'Name',
			dataIndex: 'name',
			key: 'name',
			sorter: (a, b) => {
				if (a.name.toLowerCase() < b.name.toLowerCase()) return -1;
				if (a.name.toLowerCase() > b.name.toLowerCase()) return 1;
				return 0;
			},
			...getColumnSearchProps('name'),
		},
		{
			title: 'City',
			dataIndex: 'city',
			key: 'city',
			sorter: (a, b) => {
				if (a.city.toLowerCase() < b.city.toLowerCase()) return -1;
				if (a.city.toLowerCase() > b.city.toLowerCase()) return 1;
				return 0;
			},
			...getColumnSearchProps('city'),
		},
		{
			title: 'Phone',
			dataIndex: 'phone',
			key: 'phone',
			sorter: (a, b) => {
				if (a.phone.toLowerCase() < b.phone.toLowerCase()) return -1;
				if (a.phone.toLowerCase() > b.phone.toLowerCase()) return 1;
				return 0;
			},
			...getColumnSearchProps('phone'),
		},
		{
			title: 'Email',
			dataIndex: 'email',
			key: 'email',
			sorter: (a, b) => {
				if (a.email.toLowerCase() < b.email.toLowerCase()) return -1;
				if (a.email.toLowerCase() > b.email.toLowerCase()) return 1;
				return 0;
			},
			...getColumnSearchProps('email'),
		},
		{
			title: 'CNIC',
			dataIndex: 'cnic',
			key: 'cnic',
			sorter: (a, b) => {
				if (a.cnic.toLowerCase() < b.cnic.toLowerCase()) return -1;
				if (a.cnic.toLowerCase() > b.cnic.toLowerCase()) return 1;
				return 0;
			},
			...getColumnSearchProps('cnic'),
		},
		{
			title: 'Category',
			dataIndex: 'category',
			key: 'category',
			sorter: (a, b) => {
				if (a.cnic.toLowerCase() < b.cnic.toLowerCase()) return -1;
				if (a.cnic.toLowerCase() > b.cnic.toLowerCase()) return 1;
				return 0;
			},
			...getColumnSearchProps('category'),
		},
		{
			title: 'Bank',
			dataIndex: 'bank',
			key: 'bank',
			sorter: (a, b) => {
				if (a.bank.name.toLowerCase() < b.bank.name.toLowerCase()) return -1;
				if (a.bank.name.toLowerCase() > b.bank.name.toLowerCase()) return 1;
				return 0;
			},
			render: function bank(text, record) {
				return <Space size="middle">{`${record?.bank?.name}`}</Space>;
			},
		},
		{
			title: 'Agent',
			dataIndex: 'agent',
			key: 'agent',
			sorter: (a, b) => {
				if (a.agent.toLowerCase() < b.agent.toLowerCase()) return -1;
				if (a.agent.toLowerCase() > b.agent.toLowerCase()) return 1;
				return 0;
			},
			render: function bank(text, record) {
				return (
					<Space size="middle">
						{
							<Select
								style={{ width: 100 }}
								defaultValue={record?.agent?.fullName}
								placeholder="Select a agent"
								onSelect={(val) => updateBankFormAgent(record._id, { agent: val })}
							>
								{bankUsers?.map(
									(user) =>
										user.bank?._id === record.bank?._id && (
											<Select.Option value={user._id} key={user._id}>
												{user.fullName}
											</Select.Option>
										)
								)}
							</Select>
						}
					</Space>
				);
			},
		},
		{
			title: 'Status',
			key: 'status',
			render: function status(text, record) {
				return (
					<div>
						{record?.status === BankFormStatus.APPROVED && <Tag color="green">Approved</Tag>}
						{record?.status === BankFormStatus.PENDING && <Tag color="yellow">Pending</Tag>}
						{record?.status === BankFormStatus.PROCESSING && <Tag color="yellow">Processing</Tag>}
						{record?.status === BankFormStatus.UNAPPROVED && <Tag color="warning">Unapproved</Tag>}
						{record?.status === BankFormStatus.BLOCKED && <Tag color="red">Blocked</Tag>}
					</div>
				);
			},
		},
		{
			title: 'Action',
			key: 'action',
			render: function action(text, record) {
				return (
					<>
						<Tooltip title="Update form">
							<EditOutlined
								onClick={() => {
									setIsEditModalOpen(true);
									setEditBankForm(record);
								}}
							/>
						</Tooltip>
						<Popconfirm
							title="Are you sure to delete this user?"
							onConfirm={() => onDelete(record._id)}
							okText="Yes"
							cancelText="No"
						>
							<DeleteOutlined />
						</Popconfirm>
					</>
				);
			},
		},
	];

	return (
		<div>
			<Table
				dataSource={bankForms}
				columns={columns}
				rowKey={(record) => record._id}
				size="small"
				scroll={{ x: true }}
			/>
			<Modal
				title={`Edit Form`}
				centered={true}
				visible={isEditModalOpen}
				footer={null}
				onCancel={() => setIsEditModalOpen(false)}
				destroyOnClose={true}
			>
				<Form
					name="edit-bank-form"
					initialValues={editBankForm}
					onFinish={updateForm}
					onFinishFailed={onFinishFailed}
					ref={editBankFormRef}
				>
					<Form.Item label="Name" name="name" rules={InputRules.required('Please input name!')}>
						<Input />
					</Form.Item>
					<Form.Item label="Email" name="email" rules={InputRules.required('Please input email!')}>
						<Input />
					</Form.Item>
					<Form.Item label="Phone" name="phone" rules={InputRules.required('Please input phone!')}>
						<Input />
					</Form.Item>
					<Form.Item label="CNIC" name="cnic" rules={InputRules.required('Please input cnic!')}>
						<Input />
					</Form.Item>
					<Form.Item label="City" name="city" rules={InputRules.required('Please input city!')}>
						<Input />
					</Form.Item>
					<Form.Item>
						<Button type="primary" htmlType="submit">
							Update Form
						</Button>
					</Form.Item>
				</Form>
			</Modal>
		</div>
	);
};

const mapStateToProps = (store) => ({
	bankForms: store.bankForm.bankForms,
	bankUsers: store.bankUser.bankUsers,
	banks: store.bankForm.banks,
});

const mapDispatchToProps = (dispatch) => ({
	fetchBanks: () => dispatch(Actions.fetchBanks()),
	fetchBankForms: () => dispatch(Actions.fetchBankForms()),
	fetchBankUsers: () => dispatch(Actions.fetchBankUsers()),
	deleteBankForm: (id) => dispatch(Actions.deleteBankForm(id)),
	updateBankForm: (id, body) => dispatch(Actions.updateBankForm(id, body)),
	updateBankFormStatus: (id, body) => dispatch(Actions.updateBankFormStatus(id, body)),
	updateBankFormAgent: (id, body) => dispatch(Actions.updateBankFormAgent(id, body)),
});

export default connect(mapStateToProps, mapDispatchToProps)(BankFormManagement);
